import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';

// Interfaccia per un singolo scambio nella conversazione
export interface ChatHistory {
  question: string;
  answer: string;
  timestamp: string;
}

export interface Application {
  id: number;
  label: string;
  key: string;
}

export interface ApplicationsResponse {
  applications: Application[];
}

// Interfaccia per i dati del form di feedback
export interface FeedbackData {
  history: ChatHistory[];
  satisfied: boolean;
  duration: number;
  subject?: string;
  description?: string;
  priority?: string;
  impactLevel?: string;
  urgency?: string;
  applications?: string[];
}

// Payload per l'endpoint /api/v1/issue/conversation (conversazioni semplici)
export interface CreateTicketPayload {
  history: ChatHistory[];
  satisfied: boolean;
  start_time: string;
  end_time?: string;
  duration: number;
}

// Payload per l'endpoint /api/v1/issue/request (service request completo)
export interface CreateServiceRequestPayload {
  history: ChatHistory[];
  satisfied: boolean;
  start_time: string;
  end_time?: string;
  duration: number;
  subject: string;
  description: string;
  applications: string[];
  impact_level: string;
  urgency: string;
}

export interface IssueResponse {
  key: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private readonly API_URL = '/api/v1';
  
  private readonly httpOptions = {
    headers: new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .set('Referrer-Policy', 'strict-origin-when-cross-origin'),
    withCredentials: true
  };

  // Add a flag to track if feedback is being submitted
  private isSubmittingSource = new BehaviorSubject<boolean>(false);
  public isSubmitting$ = this.isSubmittingSource.asObservable();
  
  // Fixed priority level
  public readonly FIXED_PRIORITY = 'P4';

  constructor(private http: HttpClient) {}

  // Helper method to check if currently submitting
  public isSubmitting(): boolean {
    return this.isSubmittingSource.value;
  }

  // Metodo per ticket conversazionali semplici
  submitFeedback(feedbackData: FeedbackData): Observable<IssueResponse> {
    // Set the submitting flag to true
    this.isSubmittingSource.next(true);
    
    // Calculate start and end times
    let startTime: string;
    let endTime: string = new Date().toISOString();
    
    // Get the earliest timestamp from history, or use current time if no history
    if (feedbackData.history && feedbackData.history.length > 0) {
      // Find the earliest timestamp in the history
      startTime = feedbackData.history.reduce((earliest, current) => {
        return current.timestamp < earliest ? current.timestamp : earliest;
      }, feedbackData.history[0].timestamp);
    } else {
      // If no history, use current time minus duration
      const startDate = new Date();
      startDate.setSeconds(startDate.getSeconds() - (feedbackData.duration || 0));
      startTime = startDate.toISOString();
    }
    
    // Construct request payload according to the API schema
    const conversationPayload: CreateTicketPayload = {
      history: feedbackData.history,
      satisfied: feedbackData.satisfied,
      start_time: startTime,
      end_time: endTime,
      duration: feedbackData.duration || 0
    };
    
    return this.http.post<IssueResponse>(`${this.API_URL}/issue/conversation`, conversationPayload, this.httpOptions)
      .pipe(
        finalize(() => {
          // Reset the submitting flag when the request completes (success or error)
          this.isSubmittingSource.next(false);
        })
      );
  }

  // Metodo per service request completo
  createServiceRequest(feedbackData: FeedbackData): Observable<IssueResponse> {
    // Set the submitting flag to true
    this.isSubmittingSource.next(true);
    
    // Calculate start and end times
    let startTime: string;
    let endTime: string = new Date().toISOString();
    
    // Get the earliest timestamp from history, or use current time if no history
    if (feedbackData.history && feedbackData.history.length > 0) {
      // Find the earliest timestamp in the history
      startTime = feedbackData.history.reduce((earliest, current) => {
        return current.timestamp < earliest ? current.timestamp : earliest;
      }, feedbackData.history[0].timestamp);
    } else {
      // If no history, use current time minus duration
      const startDate = new Date();
      startDate.setSeconds(startDate.getSeconds() - (feedbackData.duration || 0));
      startTime = startDate.toISOString();
    }
    
    // Verifica che siano presenti i campi richiesti
    if (!feedbackData.subject || !feedbackData.description || !feedbackData.applications || 
        !feedbackData.impactLevel || !feedbackData.urgency) {
      console.error('Missing required fields for service request');
      throw new Error('Missing required fields for service request');
    }
    
    // Construct request payload according to the API schema
    const serviceRequestPayload: CreateServiceRequestPayload = {
      history: feedbackData.history,
      satisfied: feedbackData.satisfied,
      start_time: startTime,
      end_time: endTime,
      duration: feedbackData.duration || 0,
      subject: feedbackData.subject,
      description: feedbackData.description,
      applications: feedbackData.applications,
      impact_level: feedbackData.impactLevel,
      urgency: feedbackData.urgency
    };
    
    return this.http.post<IssueResponse>(`${this.API_URL}/issue/request`, serviceRequestPayload, this.httpOptions)
      .pipe(
        finalize(() => {
          // Reset the submitting flag when the request completes (success or error)
          this.isSubmittingSource.next(false);
        })
      );
  }

  getApplications(query?: string, page = 0, pageSize = 20): Observable<ApplicationsResponse> {
    // Build URL with only the query parameter as specified in the API docs
    let url = `${this.API_URL}/applications`;
    
    if (query) {
      url += `?query=${encodeURIComponent(query)}`;
    }
    
    // Note: The actual API doesn't support pagination parameters (page, pageSize)
    // but we'll handle pagination client-side after getting the full list
    
    return this.http.get<ApplicationsResponse>(url, this.httpOptions);
  }
} 